import { ChangeEvent, useRef } from 'react'

import { clsx } from 'clsx'

import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { useThemeContext } from '@tribeplatform/react-ui-kit/ThemeProvider'

import { useCropImageModal } from '../../CropImageModal/useCropImageModal.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'

import { UploadableImage } from './types.js'

export type AvatarInputProps = {
  id: string
  image: UploadableImage
  onChange(file: File): void
  className?: string
  cropable?: boolean
  ariaLabel?: string
}
export const VariantAvatar = (props: AvatarInputProps) => {
  const { id, onChange, image, className, cropable = false, ariaLabel } = props

  const { $t } = useI18n()
  const { style } = useThemeContext()
  const fileInputRef = useRef<HTMLInputElement>()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isLoading = !!image?.isLoading

  const { onSelectFile, children: cropModal } = useCropImageModal({
    onSuccess: ({ croppedImage }) => onChange?.(croppedImage),
    options: {
      shape: style.avatarShape === 'full-rounded' ? 'round' : 'rect',
      format: 'image/png',
    },
    returnFocusRef: buttonRef,
  })

  const onChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(e.target.files)?.[0] as File
    // allow to select same file twice
    e.target.value = ''

    onSelectFile(file)
    if (!cropable) {
      onChange?.(file)
    }
  }

  return (
    <>
      <div
        className={clsx(
          'hover:bg-surface-hovered rounded-button overflow-hidden relative w-32 h-32',
          className,
        )}
      >
        <Avatar size="8x" src={image?.urls?.small || image?.url} />
        <input
          type="file"
          className="hidden"
          multiple={false}
          ref={fileInputRef}
          onChange={onChangeFile}
          accept="image/*"
        />
        <button
          id={id}
          ref={buttonRef}
          type="button"
          aria-disabled={isLoading || undefined}
          aria-busy={isLoading || undefined}
          aria-label={
            ariaLabel ??
            $t({ id: 'Generics.ChangeImage', defaultMessage: 'Change image' })
          }
          onClick={() => !isLoading && fileInputRef.current?.click()}
          className={clsx(
            'flex items-center justify-center text-white bg-background-backdrop opacity-0 hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring focus-visible:ring-inset absolute top-0 bottom-0 start-0 end-0 cursor-pointer aria-disabled:cursor-default aria-disabled:opacity-0',
            'rounded-avatar',
          )}
        >
          <T id="Generics.Change.Verb" defaultMessage="Change" />
        </button>
        {isLoading && (
          <div
            className={clsx(
              'flex items-center justify-center text-white bg-background-backdrop absolute top-0 bottom-0 start-0 end-0 pointer-events-none',
              'rounded-avatar',
            )}
          >
            <SvgIcon className="animate-spin h-7 w-7" name="spinner" />
          </div>
        )}
      </div>
      {cropable && cropModal}
    </>
  )
}
